import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { createSalesContact, updateSalesContact, searchContacts } from '../../../api/sales';
import { getUserInfoDetails } from '../../../utlis';
import axiosInstance from '../../../api/axiosInstance';
import SalesContactsHeader from './components/SalesContactsHeader';
import SalesContactsTable from './components/SalesContactsTable';
import SalesContactsModal from './components/SalesContactsModal';

const { companyId, clientId } = getUserInfoDetails();

const SalesContacts = ({ onSelectContact, isSelectionMode = false }) => {
  const location = useLocation();

  // Initial contact state
  const initialSalesContact = {
    firstName: '',
    middleName: '',
    lastName: '',
    preferredName: '',
    email: '',
    jobTitle: '',
    mobileNum1: '',
    mobileNum2: '',
    fax: '',
    contactType: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    country: '',
    zipCode: '',
    notes: '',
    vendorStatus: 'Active',
  };

  // State management
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [pageSize, setPageSize] = useState(15);
  const [searchTerm, setSearchTerm] = useState('');
  const [backendResults, setBackendResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const searchTimerRef = useRef(null);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentContact, setCurrentContact] = useState(initialSalesContact);

  // Filter state
  const [isFilterPopupOpen, setFilterPopupOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState([]);
  const [pendingEditContactId, setPendingEditContactId] = useState(null);

  // Fetch data
  const getData = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(
        '/contacts?status=Active&contactType=Vendor,Accounting,Other'
      );
      const records = res.data.data.records || [];
      setData(records);
      setAppliedFilters([]);
      setFilteredData(records);
    } catch (err) {
      toast.error('Something Went Wrong');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, []);

  // Handle edit contact from external navigation (e.g., from Vendors page)
  useEffect(() => {
    if (location.state?.editContactId) {
      setPendingEditContactId(location.state.editContactId);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    if (pendingEditContactId && data.length > 0) {
      openModalForUpdate(pendingEditContactId);
      setPendingEditContactId(null);
    }
  }, [pendingEditContactId, data]);

  // Modal handlers
  const openModalForNewContact = () => {
    setIsEditMode(false);
    setCurrentContact(initialSalesContact);
    setIsModalOpen(true);
  };

  const openModalForUpdate = (contact_id) => {
    const d = data.find((item) => item.contact_id === contact_id);
    if (!d) {
      toast.error('Contact not found');
      return;
    }

    const createdDate = new Date(d.created);
    const updatedDate = new Date(d.updated);

    const contactData = {
      firstName: d.first_name,
      middleName: d.middle_name,
      lastName: d.last_name,
      preferredName: d.preferred_name,
      email: d.email,
      jobTitle: d.job_title,
      mobileNum1: d?.phone?.mobile,
      mobileNum2: d?.phone?.phone,
      fax: d?.phone?.Fax || '',
      contactType: d.contact_type || '',
      address1: d.address?.address_line1,
      address2: d.address?.address_line2,
      city: d.address?.city,
      state: d.address?.state,
      country: d.address?.country,
      zipCode: d.address?.zip,
      notes: d.note || '',
      vendorStatus: d.status || 'Active',
      contact_id: d.contact_id,
      // Audit fields - store raw timestamps for formatting
      created: d.created || '',
      created_by: d.created_by || '',
      updated: d.updated || '',
      updated_by: d.updated_by || '',
    };

    if (isSelectionMode) {
      onSelectContact(contactData);
    } else {
      setIsEditMode(true);
      setCurrentContact(contactData);
      setIsModalOpen(true);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setIsEditMode(false);
    setCurrentContact(initialSalesContact);
  };

  // Form submission
  const handleFormSubmit = async (formData) => {
    const payload = {
      divison: 'div',
      type: formData.contactType,
      firstName: formData.firstName,
      middleName: formData.middleName,
      lastName: formData.lastName,
      email: formData.email,
      preferredName: formData.preferredName,
      jobTitle: formData.jobTitle,
      phone: {
        mobile: formData.mobileNum1,
        phone: formData.mobileNum2,
        Fax: formData.fax,
      },
      address: {
        address_line2: formData.address2,
        address_line1: formData.address1,
        city: formData.city,
        state: formData.state,
        zip: formData.zipCode,
        country: formData.country,
      },
      status: formData.vendorStatus || 'Active',
      contact_id: formData.contact_id,
    };

    try {
      await (!formData.contact_id ? createSalesContact(payload) : updateSalesContact(payload));
      closeModal();
      getData();
      toast.success(formData.contact_id ? 'Contact Updated Successfully' : 'Contact Created Successfully');
    } catch (error) {
      console.error(error);
      toast.error('Something went wrong');
    }
  };

  // Filter handlers
  const handleFilterClick = () => {
    setFilterPopupOpen(true);
  };

  const handleCloseFilterPopup = () => {
    setFilterPopupOpen(false);
  };

  const handleApplyFilters = (filters) => {
    setAppliedFilters(filters || []);
    setFilterPopupOpen(false);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
  };

  // Backend search fallback — debounced
  const performBackendSearch = useCallback(async (term) => {
    if (!term || term.length < 2) {
      setBackendResults([]);
      return;
    }
    setIsSearching(true);
    try {
      const response = await searchContacts(term);
      const records = response.data?.data?.records || [];
      setBackendResults(records);
    } catch (error) {
      setBackendResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    clearTimeout(searchTimerRef.current);

    if (!value.trim()) {
      setBackendResults([]);
      return;
    }

    searchTimerRef.current = setTimeout(() => {
      performBackendSearch(value.trim());
    }, 500);
  };

  // Filter search results
  const getSearchResults = (searchTerm, selectedCategory) => {
    if (!searchTerm || !selectedCategory || !data) return [];

    return data
      .filter((item) => {
        let valueToSearch = '';

        switch (selectedCategory) {
          case 'ID':
            valueToSearch = item.contact_id?.toString() || '';
            break;
          case 'last_name':
            valueToSearch = item.last_name || '';
            break;
          case 'Contact Name':
            valueToSearch = `${item.first_name || ''} ${item.last_name || ''}`.trim();
            break;
          case 'Contact Type':
            valueToSearch = item.contact_type || '';
            break;
          case 'Phone Number':
            valueToSearch = item.phone?.mobile || '';
            break;
          case 'Email':
            valueToSearch = item.email || '';
            break;
          case 'Address':
            valueToSearch = [
              item.address?.address_line1,
              item.address?.address_line2,
              item.address?.city,
              item.address?.state,
              item.address?.zip,
            ]
              .filter(Boolean)
              .join(', ');
            break;
          case 'Status':
            valueToSearch = item.status || '';
            break;
          default:
            valueToSearch = '';
        }

        return valueToSearch.toLowerCase().includes(searchTerm.toLowerCase());
      })
      .map((item) => {
        switch (selectedCategory) {
          case 'ID':
            return item.contact_id?.toString();
          case 'last_name':
            return item.last_name;
          case 'Contact Name':
            return `${item.first_name || ''} ${item.last_name || ''}`.trim();
          case 'Contact Type':
            return item.contact_type;
          case 'Phone Number':
            return item.phone?.mobile;
          case 'Email':
            return item.email;
          case 'Address':
            return [
              item.address?.address_line1,
              item.address?.address_line2,
              item.address?.city,
              item.address?.state,
              item.address?.zip,
            ]
              .filter(Boolean)
              .join(', ');
          case 'Status':
            return item.status;
          default:
            return null;
        }
      })
      .filter(Boolean)
      .filter((value, index, self) => self.indexOf(value) === index);
  };

  // Apply search and filters to data
  useEffect(() => {
    if (!data.length) {
      setFilteredData([]);
      return;
    }

    let result = data;

    // Apply search filter first
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter((item) => {
        const fullName = `${item.first_name || ''} ${item.last_name || ''}`.trim().toLowerCase();
        const phone = item.phone?.mobile || '';
        const address = [
          item.address?.address_line1,
          item.address?.address_line2,
          item.address?.city,
          item.address?.state,
          item.address?.zip,
        ]
          .filter(Boolean)
          .join(', ')
          .toLowerCase();

        return (
          fullName.includes(searchLower) ||
          (item.email || '').toLowerCase().includes(searchLower) ||
          phone.includes(searchLower) ||
          (item.contact_type || '').toLowerCase().includes(searchLower) ||
          address.includes(searchLower) ||
          (item.status || '').toLowerCase().includes(searchLower)
        );
      });
    }

    // Apply category filters
    if (Array.isArray(appliedFilters) && appliedFilters.length > 0) {
      result = result.filter((item) => {
        return appliedFilters.some(({ category, items }) => {
          if (!items || items.length === 0) return false;

          switch (category) {
            case 'ID':
              return items.some((id) => id === item.contact_id);
            case 'last_name':
              return items.some(
                (name) => name.toLowerCase() === (item.last_name || '').toLowerCase(),
              );
            case 'Contact Name':
              const fullName = `${item.first_name} ${item.last_name}`.trim().toLowerCase();
              return items.some((name) => name.toLowerCase() === fullName);
            case 'Contact Type':
              return items.some((type) => type === item.contact_type);
            case 'Phone Number':
              const phone = item.phone?.mobile || '';
              return items.some((number) => number === phone);
            case 'Email':
              return items.some((email) => email === item.email);
            case 'Address':
              const address = [
                item.address?.address_line1,
                item.address?.address_line2,
                item.address?.city,
                item.address?.state,
                item.address?.zip,
              ]
                .filter(Boolean)
                .join(', ')
                .toLowerCase();
              return items.some((addr) => address.includes(addr.toLowerCase()));
            case 'Status':
              return items.some((status) => status === item.status);
            default:
              return false;
          }
        });
      });
    }

    // If local search finds nothing, use backend results
    if (result.length === 0 && searchTerm.trim() && backendResults.length > 0) {
      const localIds = new Set(result.map((c) => c.contact_id));
      result = [...result, ...backendResults.filter((c) => !localIds.has(c.contact_id))];
    }

    setFilteredData(result);
  }, [appliedFilters, data, searchTerm, backendResults]);

  return (
    <div className="p-5">
      <div className="border border-gray-300 rounded-lg relative">
        {/* Header */}
        <SalesContactsHeader
          onReload={getData}
          onAddContact={openModalForNewContact}
          isLoading={isLoading}
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
        />

        {/* Backend search indicator */}
        {searchTerm.trim() && filteredData.length > 0 && backendResults.length > 0 && filteredData.some((c) => backendResults.find((b) => b.contact_id === c.contact_id)) && (
          <div className="px-4 py-2 bg-blue-50 text-blue-700 text-sm border-b border-blue-100">
            Showing results from server search for "{searchTerm}"
          </div>
        )}
        {searchTerm.trim() && isSearching && filteredData.length === 0 && (
          <div className="px-4 py-2 bg-gray-50 text-gray-500 text-sm border-b border-gray-100">
            Searching server...
          </div>
        )}

        {/* Table with loading overlay */}
        <div className="relative">
          <SalesContactsTable
            data={filteredData}
            isLoading={isLoading}
            onEditContact={openModalForUpdate}
            pageSize={pageSize}
            onPageSizeChange={handlePageSizeChange}
            isFilterPopupOpen={isFilterPopupOpen}
            onFilterClick={handleFilterClick}
            onCloseFilterPopup={handleCloseFilterPopup}
            onApplyFilters={handleApplyFilters}
            getSearchResults={getSearchResults}
          />
          {isLoading && (
            <div
              className="absolute left-0 right-0 flex flex-col items-center justify-center z-20"
              style={{ top: '64px', bottom: 0, background: 'rgba(255,255,255,0.9)' }}
            >
              {/* Spinner */}
              <div className="w-10 h-10 border-4 border-secondary-200 border-t-secondary-700 rounded-full animate-spin mb-3"></div>
              {/* Loading text with animated dots */}
              <span className="text-lg text-secondary-700 font-semibold flex items-center">
                Loading
                <span className="inline-flex ml-1">
                  <span className="animate-bounce" style={{ animationDelay: '0ms' }}>.</span>
                  <span className="animate-bounce" style={{ animationDelay: '150ms' }}>.</span>
                  <span className="animate-bounce" style={{ animationDelay: '300ms' }}>.</span>
                </span>
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      <SalesContactsModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onSubmit={handleFormSubmit}
        isEditMode={isEditMode}
        defaultValues={currentContact}
      />
    </div>
  );
};

export default SalesContacts;
